<script setup lang="ts">
import {
  Bell as BellIcon,
  Clock3 as ClockIcon,
  Copy as CopyIcon,
  Link as LinkIcon,
  Plus as PlusIcon,
  RotateCcw as RotateCcwIcon,
  Ticket as TicketIcon,
  X as XIcon,
} from '@lucide/vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import MapHeader from '@/components/MapHeader.vue'
import RoutePageHeader from '@/components/RoutePageHeader.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import { apiService } from '@/services/apiService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { webOrigin } from '@/services/platformService'
import { useSafeAreaHeaderClearance } from '@/services/safeAreaService'
import { showError, showSuccess } from '@/services/toastService'
import { useAuthStore } from '@/stores/authStore'
import type { AccountInvitation, InvitationQuotaStatus } from '@/types/accountInvitation'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const pageTopPadding = useSafeAreaHeaderClearance(48)

const isLoading = ref(true)
const isCreatingInvitation = ref(false)
const isRequestingNotification = ref(false)
const isRefreshing = ref(false)
const invitations = ref<AccountInvitation[]>([])
const status = ref<InvitationQuotaStatus | null>(null)
const pageError = ref<string | null>(null)
const invitationNote = ref('')

const INVITATION_NOTE_MAX_LENGTH = 500

const activeInvitations = computed(() =>
  invitations.value.filter((invitation) => invitation.isActive),
)
const usedInvitations = computed(() =>
  invitations.value.filter((invitation) => !!invitation.usedAt),
)
const revokedInvitations = computed(() =>
  invitations.value.filter((invitation) => !!invitation.revokedAt),
)

const canRequestNotification = computed(
  () =>
    !!status.value &&
    !status.value.canCreateInvitations &&
    !status.value.notifyWhenAvailableRequested,
)

const invitationNoteCharacterCount = computed(() => invitationNote.value.length)

const limitSummary = computed(() => {
  if (!status.value) return ''
  if (status.value.isAdmin) {
    return 'You can create invitation codes without a limit.'
  }

  const parts = [`${status.value.activeInvitationCount} yours active`]
  if (
    status.value.availableInvitationCount !== undefined &&
    status.value.availableInvitationCount !== null
  ) {
    parts.push(`${status.value.availableInvitationCount} available`)
  }
  if (
    status.value.personalActiveInviteLimitOverride !== undefined &&
    status.value.personalActiveInviteLimitOverride !== null
  ) {
    parts.push(`personal override ${status.value.personalActiveInviteLimit}`)
  }
  return parts.join(' • ')
})

const blockingReasonText = (reason: string) => {
  if (reason === 'global_limit_reached') {
    return "Currently, we don't accept new invite codes. Please come back later or request a notification when new invites are available."
  }
  if (reason === 'personal_limit_reached') {
    return 'You have reached your personal active invitation limit.'
  }
  return reason
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

const copyInvitationCode = async (code: string) => {
  try {
    await navigator.clipboard.writeText(code)
    showSuccess('Invitation code copied.')
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'Failed to copy invitation code', { error, code })
    showError('Failed to copy invitation code.')
  }
}

const invitationSignupLink = (code: string) => {
  const target = router.resolve({
    name: 'account-invite-signup',
    params: { inviteCode: code },
  })
  return `${webOrigin()}${target.href}`
}

const copyInvitationLink = async (code: string) => {
  const link = invitationSignupLink(code)
  try {
    await navigator.clipboard.writeText(link)
    showSuccess('Invitation link copied.')
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'Failed to copy invitation link', { error, code, link })
    showError('Failed to copy invitation link.')
  }
}

const loadPage = async ({ silent = false } = {}) => {
  if (!authStore.isAuthenticated) {
    await router.replace({ path: route.path, query: { ...route.query, auth: 'login' } })
    return
  }

  if (!silent) {
    isLoading.value = true
  }
  pageError.value = null

  try {
    const [nextStatus, nextInvitations] = await Promise.all([
      apiService.getInvitationQuotaStatus({ suppressToast: true }),
      apiService.getAccountInvitations({ suppressToast: true }),
    ])
    status.value = nextStatus
    invitations.value = nextInvitations
  } catch (error) {
    pageError.value = error instanceof Error ? error.message : 'Failed to load invitations'
    logService.error(LOG_CATEGORIES.API, 'Failed to load invitations page', { error })
  } finally {
    isLoading.value = false
    isRefreshing.value = false
  }
}

const createInvitation = async () => {
  isCreatingInvitation.value = true
  try {
    const response = await apiService.createAccountInvitation(
      { note: invitationNote.value.trim() || undefined },
      { suppressToast: true },
    )
    showSuccess(response.message)
    invitationNote.value = ''
    await loadPage({ silent: true })
  } catch (error) {
    showError(error instanceof Error ? error.message : 'Failed to create invitation')
  } finally {
    isCreatingInvitation.value = false
  }
}

const revokeInvitation = async (invitationId: number) => {
  try {
    const response = await apiService.revokeAccountInvitation(invitationId, { suppressToast: true })
    showSuccess(response.message)
    await loadPage({ silent: true })
  } catch (error) {
    showError(error instanceof Error ? error.message : 'Failed to revoke invitation')
  }
}

const requestNotification = async () => {
  isRequestingNotification.value = true
  try {
    const response = await apiService.requestInviteAvailabilityNotification({ suppressToast: true })
    showSuccess(response.message)
    await loadPage({ silent: true })
  } catch (error) {
    showError(error instanceof Error ? error.message : 'Failed to request notification')
  } finally {
    isRequestingNotification.value = false
  }
}

const refreshPage = async () => {
  isRefreshing.value = true
  await loadPage({ silent: true })
}

onMounted(() => {
  void loadPage()
})

watch(
  () => authStore.isAuthenticated,
  (isAuthenticated, wasAuthenticated) => {
    if (isAuthenticated !== wasAuthenticated) {
      void loadPage({ silent: isAuthenticated })
    }
  },
)
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-[#FEFEFE] text-stone-900">
    <MapHeader />

    <main class="scrollable-container min-h-0 flex-1" :style="{ paddingTop: pageTopPadding }">
      <RoutePageHeader
        eyebrow="Account"
        title="Invitations"
        description="Create invite codes for new routecamp accounts."
      >
        <template #actions>
          <button
            class="inline-flex min-h-11 items-center justify-center rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:cursor-pointer hover:border-stone-400 hover:text-stone-900"
            :disabled="isRefreshing || isLoading"
            @click="refreshPage"
          >
            <RotateCcwIcon class="mr-2 h-4 w-4" />
            {{ isRefreshing ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button
            class="inline-flex min-h-11 items-center justify-center rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:cursor-pointer hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-emerald-400"
            :disabled="isCreatingInvitation || !status?.canCreateInvitations"
            @click="createInvitation"
          >
            <PlusIcon class="mr-2 h-4 w-4" />
            {{ isCreatingInvitation ? 'Creating...' : 'Create Invite Code' }}
          </button>
        </template>
      </RoutePageHeader>

      <div class="mx-auto max-w-5xl px-5 pb-16 pt-8 sm:px-8">
        <div
          v-if="isLoading"
          class="mt-8 rounded-2xl border border-stone-200 bg-white p-8 shadow-sm"
        >
          <p class="text-sm text-stone-600">Loading invitations...</p>
        </div>

        <div
          v-else-if="pageError"
          class="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm"
          role="alert"
        >
          <p class="text-sm font-medium text-red-700">{{ pageError }}</p>
        </div>

        <template v-else>
          <section class="mt-8">
            <div class="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
              <div class="flex items-start gap-3">
                <div class="rounded-xl bg-emerald-50 p-3 text-emerald-700">
                  <TicketIcon class="h-5 w-5" />
                </div>
                <div>
                  <h2 class="text-lg font-semibold text-stone-900">Invite capacity</h2>
                  <p class="mt-1 text-sm text-stone-600">{{ limitSummary }}</p>
                </div>
              </div>

              <div v-if="status?.blockingReasons.length" class="mt-5 space-y-2">
                <div
                  v-for="reason in status.blockingReasons"
                  :key="reason"
                  class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                >
                  {{ blockingReasonText(reason) }}
                </div>
              </div>

              <div
                v-else
                class="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              >
                You can create a new invite code right now.
              </div>

              <div v-if="status?.canCreateInvitations" class="mt-5">
                <label
                  for="account-invitation-note"
                  class="block text-sm font-medium text-stone-800"
                >
                  Private note <span class="font-normal text-stone-500">(optional)</span>
                </label>
                <BaseInput
                  id="account-invitation-note"
                  v-model="invitationNote"
                  multiline
                  rows="3"
                  :maxlength="INVITATION_NOTE_MAX_LENGTH"
                  :disabled="isCreatingInvitation"
                  class="mt-2"
                  placeholder="e.g. For Alex from the weekend ride group"
                />
                <div
                  class="mt-2 flex flex-col gap-1 text-xs text-stone-500 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span>Only you can see this note. It is not shown during signup.</span>
                  <span>{{ invitationNoteCharacterCount }} / {{ INVITATION_NOTE_MAX_LENGTH }}</span>
                </div>
              </div>

              <div
                v-if="status?.notifyWhenAvailableRequested && !status?.canCreateInvitations"
                class="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800"
              >
                We will email you when new invite capacity becomes available.
              </div>

              <button
                v-if="canRequestNotification"
                class="mt-4 inline-flex items-center justify-center rounded-lg border border-sky-300 bg-white px-4 py-2 text-sm font-medium text-sky-700 transition-colors hover:cursor-pointer hover:border-sky-400 hover:text-sky-900 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="isRequestingNotification"
                @click="requestNotification"
              >
                <BellIcon class="mr-2 h-4 w-4" />
                {{
                  isRequestingNotification
                    ? 'Saving...'
                    : 'Inform Me When New Invites Are Available'
                }}
              </button>
            </div>
          </section>

          <section class="mt-8 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
            <div class="flex items-center justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold text-stone-900">Active invite codes</h2>
                <p class="mt-1 text-sm text-stone-600">
                  Share these codes privately with people who should create an account.
                </p>
              </div>
              <span
                class="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700"
              >
                {{ activeInvitations.length }} active
              </span>
            </div>

            <div
              v-if="!activeInvitations.length"
              class="mt-5 rounded-xl border border-dashed border-stone-300 px-5 py-8 text-center text-sm text-stone-500"
            >
              No active invite codes yet.
            </div>

            <div v-else class="mt-5 space-y-3">
              <div
                v-for="invitation in activeInvitations"
                :key="invitation.id"
                class="flex flex-col gap-4 rounded-xl border border-stone-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div class="flex items-center gap-2">
                    <code
                      class="rounded bg-stone-100 px-2 py-1 text-sm font-semibold tracking-[0.18em] text-stone-900"
                    >
                      {{ invitation.code }}
                    </code>
                  </div>
                  <p class="mt-2 text-sm text-stone-500">
                    Created {{ formatDate(invitation.createdAt) }}
                  </p>
                  <p
                    v-if="invitation.note"
                    class="mt-3 whitespace-pre-line rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700"
                  >
                    {{ invitation.note }}
                  </p>
                </div>

                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button
                    class="inline-flex items-center justify-center rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:cursor-pointer hover:bg-stone-50 hover:text-stone-900"
                    :aria-label="`Copy invite code ${invitation.code}`"
                    @click="copyInvitationCode(invitation.code)"
                  >
                    <CopyIcon class="mr-2 h-4 w-4" />
                    Copy Code
                  </button>
                  <button
                    class="inline-flex items-center justify-center rounded-lg border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700 transition-colors hover:cursor-pointer hover:bg-emerald-50 hover:text-emerald-900"
                    :aria-label="`Copy invite link for ${invitation.code}`"
                    @click="copyInvitationLink(invitation.code)"
                  >
                    <LinkIcon class="mr-2 h-4 w-4" />
                    Copy Link
                  </button>
                  <button
                    class="inline-flex items-center justify-center rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:cursor-pointer hover:bg-red-50"
                    @click="revokeInvitation(invitation.id)"
                  >
                    <XIcon class="mr-2 h-4 w-4" />
                    Revoke
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section class="mt-8 grid gap-6 xl:grid-cols-2">
            <div class="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <h2 class="text-lg font-semibold text-stone-900">Used invite codes</h2>
                  <p class="mt-1 text-sm text-stone-600">See who joined with each invite code.</p>
                </div>
                <ClockIcon class="h-5 w-5 text-stone-400" />
              </div>

              <div v-if="!usedInvitations.length" class="mt-5 text-sm text-stone-500">
                No invite codes have been used yet.
              </div>

              <div v-else class="mt-5 space-y-3">
                <div
                  v-for="invitation in usedInvitations"
                  :key="invitation.id"
                  class="rounded-xl border border-stone-200 px-4 py-4"
                >
                  <div class="flex items-center justify-between gap-3">
                    <code
                      class="rounded bg-stone-100 px-2 py-1 text-sm font-semibold tracking-[0.18em] text-stone-900"
                    >
                      {{ invitation.code }}
                    </code>
                    <span class="text-xs font-medium uppercase tracking-wide text-emerald-700"
                      >Used</span
                    >
                  </div>
                  <p class="mt-2 text-sm text-stone-700">
                    Joined as {{ invitation.usedByProfileName || 'Unknown profile' }}
                  </p>
                  <p class="mt-1 text-sm text-stone-500">
                    Used {{ formatDate(invitation.usedAt) }}
                  </p>
                  <p
                    v-if="invitation.note"
                    class="mt-3 whitespace-pre-line rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700"
                  >
                    {{ invitation.note }}
                  </p>
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <h2 class="text-lg font-semibold text-stone-900">Revoked invite codes</h2>
                  <p class="mt-1 text-sm text-stone-600">
                    Codes you disabled before they were used.
                  </p>
                </div>
                <XIcon class="h-5 w-5 text-stone-400" />
              </div>

              <div v-if="!revokedInvitations.length" class="mt-5 text-sm text-stone-500">
                No revoked invite codes.
              </div>

              <div v-else class="mt-5 space-y-3">
                <div
                  v-for="invitation in revokedInvitations"
                  :key="invitation.id"
                  class="rounded-xl border border-stone-200 px-4 py-4"
                >
                  <div class="flex items-center justify-between gap-3">
                    <code
                      class="rounded bg-stone-100 px-2 py-1 text-sm font-semibold tracking-[0.18em] text-stone-900"
                    >
                      {{ invitation.code }}
                    </code>
                    <span class="text-xs font-medium uppercase tracking-wide text-stone-500"
                      >Revoked</span
                    >
                  </div>
                  <p class="mt-2 text-sm text-stone-500">
                    Revoked {{ formatDate(invitation.revokedAt) }}
                  </p>
                  <p
                    v-if="invitation.note"
                    class="mt-3 whitespace-pre-line rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700"
                  >
                    {{ invitation.note }}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>
    </main>
  </div>
</template>
